import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { config } from '../Util/common';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { SUB_AUCTION_SCHEDULES } from 'Util/subscriptions';
import { useSubscription } from '@apollo/client';

const Container = styled.div`
    position: absolute;
    display: flex;
    gap: 5px;
    right: 250px;
    top: 408px;
`
const LabelText = styled.h3({
    fontWeight: 'bold',
})

const TimerText = styled.h3({
    fontWeight: 'bold',
    color: '#ff0000'
},
    variant({
        prop: 'status',
        variants: {
            prebid: { color: '#ff0000' },
            auction: { color: '#00b050' },
            counter: { color: '#ff0000' },
            results: { color: '#ed7014' }
        }
    })
)

const ErrorText = styled.h3({
    fontWeight: 'bold',
    color: '#ed7014'
})

/** Server heartbeat is every 5s; 3 missed heartbeats ⇒ the feed is stale. */
const STALE_MS = 15000
const MAX_BACKOFF_MS = 30000

/**
 * The phase WebSocket lives on the scheduler service. The old code read
 * REACT_APP_WS_WEBHOOK_ENDPOINT, which is not defined in cortex's .env (only
 * REACT_APP_WEBHOOK_ENDPOINT is) — so the socket URL was `undefined/websocket`
 * and never connected. Derive the ws(s):// origin from whichever is defined.
 */
const WS_BASE = (
    process.env.REACT_APP_WS_WEBHOOK_ENDPOINT ||
    process.env.REACT_APP_WEBHOOK_ENDPOINT ||
    ''
).replace(/^http/, 'ws')

const statusLabels = status => {
    if (status === 'prebid') { return "Prebidding: " }
    if (status === 'auction') { return "Auction in-progress: " }
    if (status === 'counter') { return "Offer phase: " }
    if (status === 'results') { return "Auction results: " }
    return ""
}

/** HH:mm:ss, prefixed with a day count when the remaining time exceeds 24h. */
const formatDuration = (totalSeconds) => {
    const days = Math.floor(totalSeconds / 86400)
    const rest = totalSeconds % 86400
    const hhmmss = moment.utc(rest * 1000).format('HH:mm:ss')
    return days > 0 ? `${days}d ${hhmmss}` : hhmmss
}

function AuctionTimer() {

    const [schedules, setSchedules] = useState([])
    const [phase, setPhase] = useState(null)        // { status, phaseEndsAt } | null
    const [clockOffset, setClockOffset] = useState(0) // serverNow - Date.now(), ms
    const [wsStatus, setWsStatus] = useState('connecting')
    const [nextAuctionStartsAt, setNextAuctionStartsAt] = useState(null) // server ISO
    const [remaining, setRemaining] = useState(null) // whole seconds to display

    /** Upcoming schedules (source for the "next auction" countdown). */
    useSubscription(SUB_AUCTION_SCHEDULES, {
        variables: { order_by: { datetime: 'asc' } },
        onSubscriptionData({ subscriptionData: { data } }) {
            if (data && data.auction_schedules) setSchedules(data.auction_schedules)
        }
    })

    /** Resilient scheduler WebSocket: reconnect w/ backoff + stale watchdog. */
    useEffect(() => {
        let socket = null
        let keepAlive = null
        let reconnectTimer = null
        let staleTimer = null
        let attempts = 0
        let disposed = false

        const url = `${WS_BASE}/websocket`

        const armStale = () => {
            if (staleTimer) clearTimeout(staleTimer)
            staleTimer = setTimeout(() => setWsStatus('error'), STALE_MS)
        }

        const handle = (raw) => {
            let data
            try { data = JSON.parse(raw) } catch { return }
            if (!data) return
            // Any frame with serverNow re-syncs the clock offset.
            if (data.serverNow) {
                setClockOffset(new Date(data.serverNow).getTime() - Date.now())
            }
            // Server-authoritative next-auction start.
            if ('nextAuctionStartsAt' in data) {
                setNextAuctionStartsAt(data.nextAuctionStartsAt || null)
            }
            // Frames carrying a `status` key describe the phase (present/absent).
            if ('status' in data) {
                if (data.status && data.status !== 'closed' && data.phaseEndsAt) {
                    setPhase({ status: data.status, phaseEndsAt: data.phaseEndsAt })
                } else {
                    setPhase(null)
                }
            }
        }

        const connect = () => {
            if (disposed) return
            setWsStatus(s => (s === 'error' ? s : 'connecting'))
            armStale()

            socket = new WebSocket(url)

            socket.onopen = () => {
                attempts = 0
                keepAlive = setInterval(() => {
                    if (socket && socket.readyState === WebSocket.OPEN) {
                        socket.send('ping from web')
                    }
                }, 30000)
            }

            socket.onmessage = (event) => {
                setWsStatus('open')
                armStale()
                handle(event.data)
            }

            socket.onclose = () => {
                if (keepAlive) clearInterval(keepAlive)
                if (disposed) return
                setWsStatus(s => (s === 'error' ? s : 'connecting'))
                const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempts)
                attempts += 1
                reconnectTimer = setTimeout(connect, delay)
            }

            socket.onerror = () => { if (socket) socket.close() }
        }

        connect()

        return () => {
            disposed = true
            if (keepAlive) clearInterval(keepAlive)
            if (reconnectTimer) clearTimeout(reconnectTimer)
            if (staleTimer) clearTimeout(staleTimer)
            if (socket) socket.close()
        }
    }, [])

    /**
     * Re-derive the displayed seconds every tick from the absolute target
     * (phase end, or next auction datetime) and the server clock offset —
     * never a stored -1 counter. Immune to device-clock skew and drift.
     */
    useEffect(() => {
        const nextAuction = _.first(_.filter(schedules, ['status', 'scheduled']))

        // Prefer the server-broadcast absolute start (identical across clients);
        // fall back to the Hasura schedule only when the server didn't send one.
        const nextTargetMs = nextAuctionStartsAt
            ? new Date(nextAuctionStartsAt).getTime()
            : (nextAuction && nextAuction.datetime
                ? moment.tz(nextAuction.datetime, config.timezone).valueOf()
                : null)

        const compute = () => {
            const serverNow = Date.now() + clockOffset
            if (phase && phase.phaseEndsAt) {
                const ms = new Date(phase.phaseEndsAt).getTime() - serverNow
                return Math.max(Math.round(ms / 1000), 0)
            }
            if (nextTargetMs != null) {
                return Math.max(Math.round((nextTargetMs - serverNow) / 1000), 0)
            }
            return null
        }

        setRemaining(compute())
        const id = setInterval(() => setRemaining(compute()), 1000)
        return () => clearInterval(id)
    }, [phase, clockOffset, schedules, nextAuctionStartsAt])

    // On a sync failure *during a live phase*, surface a fallback and pause the
    // countdown. When idle we fall through to the next-auction countdown.
    if (wsStatus === 'error' && phase) {
        return (
            <Container>
                <ErrorText>Connection error - unable to sync timer</ErrorText>
            </Container>
        )
    }

    let labelText = ""
    let timeStr = ""
    let status

    if (phase && remaining != null) {
        labelText = statusLabels(phase.status)
        status = phase.status
        timeStr = formatDuration(remaining)
    } else if (remaining != null && remaining > 0) {
        // Only while the next auction is genuinely in the future — a past start
        // must not linger as a stuck 00:00:00.
        labelText = "Next Auction starts in"
        timeStr = formatDuration(remaining)
    } else {
        labelText = "No Scheduled Auctions"
        timeStr = ""
    }

    return (
        <Container>
            <LabelText>{labelText} </LabelText>
            <TimerText status={status}>{timeStr}</TimerText>
        </Container>
    )
}

export default AuctionTimer
